import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts'
import {
  Box, Text, Group, Badge, Center, SimpleGrid, Stack, Paper, Divider, Table, Progress, Button,
} from '@mantine/core'
import { IconTicket, IconBottle, IconClock, IconCalendar, IconX } from '@tabler/icons-react'
import SmartDateInput from '../SmartDateInput'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { useWorkOrderMeta } from '../../hooks/useWorkOrderMeta'

// ── สรุปงานแจ้งซ่อม (Work Order) / คำขอของใช้ (HK Order) ตามช่วงเวลา — ย้ายมาจากหน้า Reports > Work/HK Orders
// (ตามที่ขอ: การสรุปอยู่ที่ Dashboard ส่วนหน้า Report จะเป็นหน้ากรอง/ดึงรายงานแทน) ──

const STATUS_COLORS = { OPEN: '#f59e0b', IN_PROGRESS: '#3b82f6', DONE: '#0d9488', CANCELLED: '#94a3b8' }

function KPI({ label, value, color = '#0f172a' }: { label: string; value: any; color?: string }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>{label}</Text>
      <Text style={{ fontSize: 22, fontWeight: 700, color, fontFamily: 'Inter,sans-serif', letterSpacing: '-0.5px', lineHeight: 1 }}>{value}</Text>
    </Paper>
  )
}

function StatusDonut({ data, total, colorMap, labelMap, centerLabel, height = 160 }: {
  data: Record<string, number>; total: number; colorMap: Record<string, string>
  labelMap?: Record<string, string>; centerLabel: string; height?: number
}) {
  const entries = Object.entries(data).filter(([, v]) => v > 0).sort(([, a], [, b]) => b - a)
  const chartData = entries.map(([k, v]) => ({ name: labelMap?.[k] ?? k, key: k, value: v }))
  if (entries.length === 0) return <Center h={height}><Text size="xs" c="dimmed">—</Text></Center>
  return (
    <Group align="center" gap="lg" wrap="nowrap">
      <Box style={{ position: 'relative', width: height, height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="90%" paddingAngle={2} stroke="none">
              {chartData.map((d, i) => <Cell key={i} fill={colorMap[d.key] ?? '#94a3b8'} />)}
            </Pie>
            <Tooltip formatter={(v: any, n: any) => [v, n]} />
          </PieChart>
        </ResponsiveContainer>
        <Box style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Text style={{ fontSize: 26, fontWeight: 700, fontFamily: 'Inter,sans-serif', color: '#0f172a', lineHeight: 1 }}>{total}</Text>
          <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginTop: 2 }}>{centerLabel}</Text>
        </Box>
      </Box>
      <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
        {entries.map(([k, v]) => (
          <Group key={k} justify="space-between" gap={8} wrap="nowrap">
            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
              <Box style={{ width: 8, height: 8, borderRadius: 2, background: colorMap[k] ?? '#94a3b8', flexShrink: 0 }} />
              <Text size="xs" truncate>{labelMap?.[k] ?? k}</Text>
            </Group>
            <Text size="xs" fw={600} c="dimmed" style={{ flexShrink: 0 }}>{v} · {total ? Math.round(v / total * 100) : 0}%</Text>
          </Group>
        ))}
      </Stack>
    </Group>
  )
}

function TopBarChart({ data, limit = 7, color = '#0d9488', height = 160 }: {
  data: Record<string, number>; limit?: number; color?: string; height?: number
}) {
  const chartData = Object.entries(data)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([name, value]) => ({ name: name.length > 18 ? name.slice(0, 17) + '…' : name, value }))
    .reverse()
  if (chartData.length === 0) return <Center h={height}><Text size="xs" c="dimmed">—</Text></Center>
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} layout="vertical" margin={{ left: 4, right: 16, top: 4, bottom: 4 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v: any) => [v, '']} labelStyle={{ fontSize: 12 }} />
        <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} barSize={14} />
      </BarChart>
    </ResponsiveContainer>
  )
}

const countByStatus = (rows: any[]) => ({
  OPEN: rows.filter(r => r.status === 'OPEN').length,
  IN_PROGRESS: rows.filter(r => r.status === 'IN_PROGRESS').length,
  DONE: rows.filter(r => r.status === 'DONE').length,
  CANCELLED: rows.filter(r => r.status === 'CANCELLED').length,
})

const avgCloseHours = (rows: any[]) => {
  const closed = rows.filter(r => r.status === 'DONE' && r.closedAt && r.createdAt)
  if (!closed.length) return null
  const totalMs = closed.reduce((sum, r) => sum + (new Date(r.closedAt).getTime() - new Date(r.createdAt).getTime()), 0)
  return totalMs / closed.length / (1000 * 60 * 60)
}

const countByKey = (rows: any[], key: string, fallback = '—') => {
  const map: Record<string, number> = {}
  rows.forEach(r => { const k = r[key] || fallback; map[k] = (map[k] ?? 0) + 1 })
  return Object.entries(map).sort((a, b) => b[1] - a[1])
}

const formatHours = (h: number | null) => {
  if (h === null) return '—'
  if (h < 1) return `${Math.round(h * 60)} นาที`
  if (h < 24) return `${h.toFixed(1)} ชม.`
  return `${(h / 24).toFixed(1)} วัน`
}

export default function OrderSummarySection({ workOrders, hkOrders }: { workOrders: any[]; hkOrders: any[] }) {
  const { t } = useTranslation()
  const { catLabel, deptLabel, fmtDateTime } = useWorkOrderMeta()
  const { isMobile } = useBreakpoint()
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  const inPeriod = (dateStr: string | undefined | null): boolean => {
    if (!dateFrom && !dateTo) return true
    if (!dateStr) return false
    const d = dateStr.slice(0, 10)
    if (dateFrom && d < dateFrom) return false
    if (dateTo && d > dateTo) return false
    return true
  }

  const s = useMemo(() => {
    const wo = workOrders.filter(w => inPeriod(w.createdAt))
    const hko = hkOrders.filter(w => inPeriod(w.createdAt))
    const loanItems = hko.filter((h: any) => h.isLoan)
    return {
      wo, hko, loanItems,
      woStatus: countByStatus(wo), hkoStatus: countByStatus(hko),
      woAvg: avgCloseHours(wo), hkoAvg: avgCloseHours(hko),
      woByCategory: countByKey(wo, 'category'),
      woByDepartment: countByKey(wo, 'departmentCode', ''),
      woLatest: [...wo].sort((a: any, b: any) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')).slice(0, 10),
      hkoByItem: countByKey(hko, 'itemName', 'ไม่ระบุ'),
      loanReturn: {
        RETURNED: loanItems.filter((h: any) => h.returned).length,
        NOT_RETURNED: loanItems.filter((h: any) => !h.returned).length,
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrders, hkOrders, dateFrom, dateTo])

  const labelMap = {
    OPEN: t('workOrders.statusOpen', 'Open'), IN_PROGRESS: t('workOrders.statusInProgress', 'In Progress'),
    DONE: t('workOrders.statusDone', 'Done'), CANCELLED: t('workOrders.statusCancelled', 'Cancelled'),
  }
  const hasPeriod = !!(dateFrom || dateTo)

  return (
    <Box mt="xl">
      <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 10 }}>
        {t('facility.periodSummary', 'สรุปตามช่วงเวลา')}
      </Text>

      <Paper withBorder radius="md" p="sm" mb="md" style={{ borderColor: hasPeriod ? '#6366f1' : '#e2e8f0', background: hasPeriod ? '#f5f3ff' : undefined }}>
        <Group gap="sm" align="flex-end" wrap="wrap">
          <IconCalendar size={18} color={hasPeriod ? '#6366f1' : '#94a3b8'} style={{ marginBottom: 6 }} />
          <SmartDateInput label={t('reports.dateFrom', 'จากวันที่')} value={dateFrom} onChange={setDateFrom} />
          <SmartDateInput label={t('reports.dateTo', 'ถึงวันที่')} value={dateTo} onChange={setDateTo} />
          {hasPeriod && (
            <Button variant="subtle" color="gray" size="xs" leftSection={<IconX size={12} />} onClick={() => { setDateFrom(''); setDateTo('') }}>
              {t('common.clear', 'ล้าง')}
            </Button>
          )}
          {!hasPeriod && <Text size="xs" c="dimmed" style={{ marginBottom: 8 }}>{t('facility.allTime', 'แสดงทุกช่วงเวลา')}</Text>}
        </Group>
      </Paper>

      {/* ── Work Order ── */}
      <Group gap={6} mb="xs">
        <IconTicket size={16} color="#0f172a" />
        <Text fw={700} size="sm">{t('reports.workOrderSection', 'Work Order')}</Text>
      </Group>
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Paper withBorder radius="md" p="md">
          <Text fw={600} size="sm" mb="md">{t('reports.workOrderStatusChart', 'Work Orders by Status')}</Text>
          <StatusDonut data={s.woStatus} total={s.wo.length} colorMap={STATUS_COLORS} labelMap={labelMap} centerLabel={t('common.total', 'Total')} />
        </Paper>
        <Paper withBorder radius="md" p="md">
          <Text fw={600} size="sm" mb="md">{t('reports.byCategory', 'By Category')}</Text>
          <TopBarChart data={Object.fromEntries(s.woByCategory.map(([cat, count]) => [catLabel(cat), count]))} color="#0d9488" />
        </Paper>
        <Paper withBorder radius="md" p="md">
          <Text fw={600} size="sm" mb="md">{t('workOrders.byDepartment', 'Work Orders by Department')}</Text>
          <TopBarChart data={Object.fromEntries(s.woByDepartment.map(([dept, count]) => [dept ? deptLabel(dept) : t('workOrders.noDepartment', 'No department'), count]))} color="#4f46e5" />
        </Paper>
      </SimpleGrid>
      <SimpleGrid cols={{ base: 2, md: 6 }} mb="md">
        <KPI label={labelMap.OPEN} value={s.woStatus.OPEN} color="#f59e0b" />
        <KPI label={labelMap.IN_PROGRESS} value={s.woStatus.IN_PROGRESS} color="#3b82f6" />
        <KPI label={labelMap.DONE} value={s.woStatus.DONE} color="#0d9488" />
        <KPI label={labelMap.CANCELLED} value={s.woStatus.CANCELLED} color="#94a3b8" />
        <KPI label={t('reports.total', 'Total')} value={s.wo.length} />
        <KPI label={t('reports.avgCloseTime', 'Avg. Close Time')} value={formatHours(s.woAvg)} color="#8b5cf6" />
      </SimpleGrid>

      {s.woLatest.length > 0 && (
        <Box mb="md" style={{ overflowX: 'auto' }}>
          <Text size="xs" fw={700} c="dimmed" mb={6} style={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('workOrders.latestOrders', 'Latest Work Orders')}</Text>
          <Table striped highlightOnHover withTableBorder miw={isMobile ? 640 : undefined}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('workOrders.colCreated', 'Issued (date/time)')}</Table.Th>
                <Table.Th>{t('workOrders.colDepartment', 'Department')}</Table.Th>
                <Table.Th>{t('workOrders.colTitle', 'Job')}</Table.Th>
                <Table.Th>{t('workOrders.colCategory', 'Category')}</Table.Th>
                <Table.Th>{t('workOrders.colLocation', 'Location')}</Table.Th>
                <Table.Th>{t('common.status', 'Status')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {s.woLatest.map((w: any) => (
                <Table.Tr key={w.id}>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(w.createdAt)}</Table.Td>
                  <Table.Td>{w.departmentCode ? deptLabel(w.departmentCode) : <Text span size="xs" c="dimmed">{t('workOrders.noDepartment', 'No department')}</Text>}</Table.Td>
                  <Table.Td>{w.title}</Table.Td>
                  <Table.Td>{catLabel(w.category)}</Table.Td>
                  <Table.Td>{w.location ?? '—'}</Table.Td>
                  <Table.Td><Badge size="xs" variant="light" color={w.status === 'DONE' ? 'teal' : w.status === 'CANCELLED' ? 'gray' : w.status === 'IN_PROGRESS' ? 'blue' : 'orange'}>{labelMap[w.status as keyof typeof labelMap] ?? w.status}</Badge></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      <Divider my="lg" />

      {/* ── HK Order ── */}
      <Group gap={6} mb="xs">
        <IconBottle size={16} color="#0f172a" />
        <Text fw={700} size="sm">{t('reports.hkOrderSection', 'HK Order')}</Text>
      </Group>
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Paper withBorder radius="md" p="md">
          <Text fw={600} size="sm" mb="md">{t('reports.hkOrderStatusChart', 'HK Orders by Status')}</Text>
          <StatusDonut data={s.hkoStatus} total={s.hko.length} colorMap={STATUS_COLORS} labelMap={labelMap} centerLabel={t('common.total', 'Total')} />
        </Paper>
        <Paper withBorder radius="md" p="md">
          <Text fw={600} size="sm" mb="md">{t('reports.byItem', 'By Item')}</Text>
          <TopBarChart data={Object.fromEntries(s.hkoByItem)} color="#7c3aed" />
        </Paper>
      </SimpleGrid>
      <SimpleGrid cols={{ base: 2, md: 6 }} mb="md">
        <KPI label={labelMap.OPEN} value={s.hkoStatus.OPEN} color="#f59e0b" />
        <KPI label={labelMap.IN_PROGRESS} value={s.hkoStatus.IN_PROGRESS} color="#3b82f6" />
        <KPI label={labelMap.DONE} value={s.hkoStatus.DONE} color="#0d9488" />
        <KPI label={labelMap.CANCELLED} value={s.hkoStatus.CANCELLED} color="#94a3b8" />
        <KPI label={t('reports.total', 'Total')} value={s.hko.length} />
        <KPI label={t('reports.avgCloseTime', 'Avg. Close Time')} value={formatHours(s.hkoAvg)} color="#8b5cf6" />
      </SimpleGrid>
      {s.hkoByItem.length > 0 && (
        <Box>
          <Text size="xs" fw={700} c="dimmed" mb={6} style={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('reports.byItem', 'By Item')}</Text>
          <Stack gap={6}>
            {s.hkoByItem.slice(0, 8).map(([item, count]) => (
              <Group key={item} gap="sm" wrap="nowrap">
                <Text size="xs" style={{ width: 110, flexShrink: 0 }} lineClamp={1}>{item}</Text>
                <Progress value={(count / s.hko.length) * 100} color="grape" flex={1} size="md" radius="sm" />
                <Text size="xs" c="dimmed" style={{ width: 30, textAlign: 'right', flexShrink: 0 }}>{count}</Text>
              </Group>
            ))}
          </Stack>
        </Box>
      )}

      <Divider my="lg" />

      {/* ── ของยืม-คืน ── */}
      <Group gap={6} mb="xs">
        <IconClock size={16} color="#0f172a" />
        <Text fw={700} size="sm">{t('reports.loanItemsSection', 'Loan Items (borrow-return)')}</Text>
      </Group>
      {s.loanItems.length === 0 ? (
        <Text size="xs" c="dimmed" mb="md">{t('reports.noLoanItems', 'No loan items in this period')}</Text>
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t('reports.loanReturnStatus', 'Return Status')}</Text>
              <StatusDonut
                data={s.loanReturn} total={s.loanItems.length}
                colorMap={{ RETURNED: '#0d9488', NOT_RETURNED: '#ef4444' }}
                labelMap={{ RETURNED: t('reports.returned', 'Returned'), NOT_RETURNED: t('reports.notReturnedYet', 'Not Returned Yet') }}
                centerLabel={t('reports.totalLoans', 'Total Loans')}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t('reports.mostLoanedItems', 'Most Loaned Items')}</Text>
              <TopBarChart
                data={s.loanItems.reduce((acc: any, h: any) => { const k = h.itemName || t('reports.unspecified', 'Unspecified'); acc[k] = (acc[k] || 0) + 1; return acc }, {})}
                limit={6} color="#7c3aed"
              />
            </Paper>
          </SimpleGrid>
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover withTableBorder mb="md" miw={isMobile ? 520 : undefined}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('common.location', 'Location')}</Table.Th>
                  <Table.Th>{t('reports.item', 'Item')}</Table.Th>
                  <Table.Th>{t('common.qty', 'Qty')}</Table.Th>
                  <Table.Th>{t('reports.loanDate', 'Loan Date')}</Table.Th>
                  <Table.Th>{t('reports.dueDate', 'Due Date')}</Table.Th>
                  <Table.Th>{t('common.status', 'Status')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {s.loanItems.map((h: any) => (
                  <Table.Tr key={h.id}>
                    <Table.Td>{h.location ?? '—'}</Table.Td>
                    <Table.Td>{h.itemName ?? '—'}</Table.Td>
                    <Table.Td>{h.qty ?? 1}</Table.Td>
                    <Table.Td>{h.createdAt?.slice(0, 10) ?? '—'}</Table.Td>
                    <Table.Td>{h.returnDueDate?.slice(0, 10) ?? '—'}</Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={h.returned ? 'teal' : 'red'} variant="light">
                        {h.returned ? t('reports.returned', 'Returned') : t('reports.notReturnedYet', 'Not Returned Yet')}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </>
      )}

      {s.wo.length === 0 && s.hko.length === 0 && (
        <Center p="xl"><Text size="sm" c="dimmed">{t('common.noRecords', 'No records found')}</Text></Center>
      )}
    </Box>
  )
}
